import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { useState } from 'react';
import type { AppError } from '@/api/client';
import { useVenueContext } from '@/providers/VenueProvider';

type RegisterForm = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const { register: authRegister } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSlug } = useVenueContext();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const from = (location.state as { from?: string })?.from;
  const prefillEmail = searchParams.get('email') || '';

  const registerSchema = z.object({
    firstName: z.string().min(1, t('validation.first_name_required')),
    lastName: z.string().min(1, t('validation.last_name_required')),
    email: z.string().email(t('validation.email_invalid')),
    password: z
      .string()
      .min(8, t('validation.password_min'))
      .regex(/[A-Z]/, t('validation.password_uppercase'))
      .regex(/[a-z]/, t('validation.password_lowercase'))
      .regex(/[0-9]/, t('validation.password_number')),
    phone: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: prefillEmail },
  });

  const onSubmit = async (data: RegisterForm) => {
    setError(null);
    try {
      await authRegister(data);
      navigate(from || `/venue/${currentSlug}/book`, { replace: true });
    } catch (err) {
      const appError = err as AppError;
      if (appError.code === 'EMAIL_EXISTS') {
        setError(t('auth.error_email_exists'));
      } else {
        setError(t('auth.error_generic'));
      }
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4 pt-6">
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('auth.first_name')}</Label>
              <Input id="firstName" autoComplete="given-name" {...register('firstName')} />
              {errors.firstName && (
                <p className="text-xs text-destructive">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('auth.last_name')}</Label>
              <Input id="lastName" autoComplete="family-name" {...register('lastName')} />
              {errors.lastName && (
                <p className="text-xs text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input id="email" type="email" autoComplete="email" {...register('email')} />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">{t('auth.phone')}</Label>
            <Input id="phone" type="tel" autoComplete="tel" {...register('phone')} />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('auth.register')}
          </Button>
        </CardContent>
        <CardFooter className="text-sm text-muted-foreground">
          {t('auth.has_account')}{' '}
          <Link to="/login" state={{ from }} className="ml-1 font-medium text-primary hover:underline">
            {t('auth.login')}
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}
